import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { quickbooksAPI } from '../services/api';

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [qbConnected, setQbConnected] = useState<boolean | null>(null);
  const [qbRealmId, setQbRealmId] = useState<string | null>(null);
  const [qbMessage, setQbMessage] = useState<string | null>(null);
  const [qbLoading, setQbLoading] = useState(false);
  const [qbDisconnecting, setQbDisconnecting] = useState(false);

  const fetchConnectionStatus = async () => {
    try {
      const res = await quickbooksAPI.getConnectionStatus();
      setQbConnected(res.data.connected);
      setQbRealmId(res.data.realm_id ?? null);
    } catch {
      setQbConnected(false);
      setQbRealmId(null);
    }
  };

  useEffect(() => {
    fetchConnectionStatus();
  }, []);

  useEffect(() => {
    const quickbooks = searchParams.get('quickbooks');
    const message = searchParams.get('message');
    if (quickbooks === 'success') {
      setQbMessage('QuickBooks Online connected successfully.');
      setSearchParams({}, { replace: true });
      fetchConnectionStatus();
    } else if (quickbooks === 'error' && message) {
      setQbMessage(`QuickBooks connection failed: ${decodeURIComponent(message)}`);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleConnectQuickbooks = async () => {
    setQbMessage(null);
    setQbLoading(true);
    try {
      const res = await quickbooksAPI.getOAuthUrl();
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
        return;
      }
      setQbMessage('Could not get QuickBooks authorization URL.');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setQbMessage(ax?.response?.data?.error || 'Failed to start QuickBooks connection.');
    } finally {
      setQbLoading(false);
    }
  };

  const handleDisconnectQuickbooks = async () => {
    setQbMessage(null);
    setQbDisconnecting(true);
    try {
      await quickbooksAPI.disconnect();
      setQbMessage('Disconnected from QuickBooks Online.');
      setQbConnected(false);
      setQbRealmId(null);
    } catch {
      setQbMessage('Failed to disconnect.');
    } finally {
      setQbDisconnecting(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Billing Settings</h2>

        <section style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>QuickBooks Online</h3>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Connect your QuickBooks Online account to export invoices from the invoice detail page.
          </p>
          {qbMessage && (
            <div
              style={{
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                backgroundColor: qbMessage.includes('failed') || qbMessage.includes('error') ? '#fff3cd' : '#d4edda',
                border: `1px solid ${qbMessage.includes('failed') || qbMessage.includes('error') ? '#ffc107' : '#28a745'}`,
                borderRadius: '4px',
              }}
            >
              {qbMessage}
            </div>
          )}
          {qbConnected === null ? (
            <p>Loading…</p>
          ) : qbConnected ? (
            <div>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Status:</strong> Connected
                {qbRealmId && (
                  <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                    (Company ID: {qbRealmId})
                  </span>
                )}
              </p>
              <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                When exporting invoices, P.O. numbers are automatically included on the QBO invoice.
              </p>
              <button
                type="button"
                onClick={handleDisconnectQuickbooks}
                disabled={qbDisconnecting}
                className="btn btn-secondary"
              >
                {qbDisconnecting ? 'Disconnecting…' : 'Disconnect QuickBooks Online'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleConnectQuickbooks}
              disabled={qbLoading}
              className="btn btn-primary"
            >
              {qbLoading ? 'Redirecting…' : 'Connect to QuickBooks Online'}
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default Settings;
